import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, DownloadOutline, Heading, HStack, Text, VStack } from '@bonusly/design-system';

import {
	DESKTOP_DOWNLOAD_URLS,
	useDetectedDesktopPlatform,
	type DetectedDesktopPlatform,
} from '../../../components/bizy-side-panel/hooks/use-bizy-desktop-download';

const AppleGlyph: FC = () => (
	<svg width="20" height="24" viewBox="0 0 20 24" fill="currentColor" aria-hidden>
		<path d="M14.6 12.7c0-2.6 2.1-3.8 2.2-3.9-1.2-1.7-3-2-3.7-2-1.6-.2-3 .9-3.8.9-.8 0-2-.9-3.3-.9-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.3 2.6 1.3-.1 1.8-.9 3.4-.9 1.6 0 2 .9 3.4.9 1.4 0 2.3-1.2 3.2-2.5 1-1.4 1.4-2.8 1.5-2.9-.1 0-2.8-1.1-2.8-4.2zM12.1 4.1c.7-.9 1.2-2.1 1.1-3.3-1 0-2.3.7-3 1.5-.7.8-1.3 2-1.1 3.2 1.1.1 2.3-.6 3-1.4z" />
	</svg>
);

const WindowsGlyph: FC = () => (
	<svg width="22" height="22" viewBox="0 0 22 22" fill="currentColor" aria-hidden>
		<path d="M0 3.1L9 1.85V10.5H0V3.1zM10 1.7L22 0v10.5H10V1.7zM0 11.5h9v7.4L0 17.65V11.5zM10 11.5h12V22l-12-1.7V11.5z" />
	</svg>
);

interface DesktopCardProps {
	icon: ReactNode;
	title: string;
	detected: boolean;
	testId: string;
	children: ReactNode;
}

const DesktopCard: FC<DesktopCardProps> = ({ icon, title, detected, testId, children }) => {
	const { t } = useTranslation('admin_bizy');
	return (
		<Box border="1px solid" borderColor="border.soft" borderRadius="xl" p={4} bg="bg.surface" data-testid={testId}>
			<HStack justifyContent="space-between" alignItems="center" mb={3}>
				<HStack spacing={3} alignItems="center">
					<Box color="text.default" display="flex" alignItems="center">
						{icon}
					</Box>
					<Heading as="h4" size="md" color="text.default">
						{title}
					</Heading>
				</HStack>
				{detected && (
					<Box bg="primary.bgAlpha" color="text.default" px={2} py={0.5} borderRadius="sm">
						<Text size="sm" fontWeight="medium">
							{t('settingsPanel.apps.detected')}
						</Text>
					</Box>
				)}
			</HStack>
			<VStack spacing={2} align="stretch">
				{children}
			</VStack>
		</Box>
	);
};

interface DownloadButtonProps {
	href: string;
	label: string;
	testId: string;
}

const DownloadButton: FC<DownloadButtonProps> = ({ href, label, testId }) => (
	<Button
		as="a"
		href={href}
		rel="noopener"
		download
		rightIcon={<DownloadOutline size="sm" />}
		variant="ghost"
		size="md"
		justifyContent="center"
		bg="secondary.bgAlpha"
		color="text.default"
		_hover={{ bg: 'secondary.bgAlphaHover' }}
		_active={{ bg: 'secondary.bgAlphaActive' }}
		data-testid={testId}
	>
		{label}
	</Button>
);

export const AppsSection: FC = () => {
	const { t } = useTranslation('admin_bizy');
	const platform: DetectedDesktopPlatform | null = useDetectedDesktopPlatform();

	const macCard = (
		<DesktopCard key="mac" icon={<AppleGlyph />} title={t('settingsPanel.apps.macOS')} detected={platform === 'mac'} testId="bizy-apps-card-mac">
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['mac-arm64']} label={t('settingsPanel.apps.downloadMacAppleSilicon')} testId="download-mac-arm64" />
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['mac-x64']} label={t('settingsPanel.apps.downloadMacIntel')} testId="download-mac-x64" />
		</DesktopCard>
	);

	const windowsCard = (
		<DesktopCard key="win" icon={<WindowsGlyph />} title={t('settingsPanel.apps.windows')} detected={platform === 'win'} testId="bizy-apps-card-win">
			<DownloadButton href={DESKTOP_DOWNLOAD_URLS['win']} label={t('settingsPanel.apps.downloadWindows')} testId="download-win" />
		</DesktopCard>
	);

	const cards = platform === 'win' ? [windowsCard, macCard] : [macCard, windowsCard];

	return (
		<VStack spacing={4} align="stretch">
			<Text size="md" fontWeight="medium" color="text.default">
				{t('settingsPanel.apps.desktop')}
			</Text>
			{cards}
		</VStack>
	);
};
